import { FormEvent, useEffect, useState } from 'react';
import {
  api,
  ApiError,
  type BasketballRules,
  type RulePreset,
  type Sport,
} from '../api';
import { AppShell } from '../components/AppShell';

function defaultRules(): BasketballRules {
  return {
    periodCount: 4,
    periodLengthSeconds: 480,
    overtimeLengthSeconds: 240,
    initialTimeouts: 5,
    bonusFoulThreshold: 7,
    personalFoulLimit: 5,
    shotClock: {
      enabled: false,
      durationSeconds: 30,
      offensiveReboundSeconds: 20,
    },
  };
}

export function GameRulesPage() {
  const [sports, setSports] = useState<Sport[] | null>(null);
  const [sportId, setSportId] = useState<string>('');
  const [presets, setPresets] = useState<RulePreset[] | null>(null);
  const [editing, setEditing] = useState<RulePreset | 'new' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api
      .listSports()
      .then((s) => {
        setSports(s);
        const bb = s.find((x) => x.slug === 'basketball') ?? s[0];
        if (bb) setSportId(bb.id);
      })
      .catch(() => setErr('failed_to_load_sports'));
  }, []);

  async function load(sId: string) {
    try {
      setErr(null);
      const list = await api.listRulePresets(sId);
      setPresets(list);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed_to_load_presets');
    }
  }

  useEffect(() => {
    if (sportId) load(sportId);
  }, [sportId]);

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Game rules</h1>
            <p className="mt-1 text-muted">
              Define the levels you run — period length, shot clock, foul
              thresholds, timeouts. When you create a game, pick a level
              from this list. The rules are snapshotted into the game, so
              editing a preset later never changes finished games.
            </p>
          </div>
          {editing == null && sportId && (
            <button
              className="btn btn-primary"
              onClick={() => setEditing('new')}
            >
              + Add level
            </button>
          )}
        </div>

        {sports && sports.length > 1 && (
          <div className="card">
            <label className="label">Sport</label>
            <select
              className="input max-w-xs"
              value={sportId}
              onChange={(e) => {
                setSportId(e.target.value);
                setEditing(null);
              }}
            >
              {sports.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {err && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {err}
          </div>
        )}

        {editing != null && sportId && (
          <PresetEditor
            sportId={sportId}
            preset={editing === 'new' ? null : editing}
            onDone={async () => {
              setEditing(null);
              await load(sportId);
            }}
            onCancel={() => setEditing(null)}
          />
        )}

        {!presets && !err && <div className="text-muted">Loading…</div>}

        {presets && presets.length > 0 && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {presets.map((p) => (
              <PresetCard
                key={p.id}
                preset={p}
                onEdit={() => setEditing(p)}
                onDeleted={async () => {
                  if (!confirm(`Delete "${p.name}"?`)) return;
                  await api.deleteRulePreset(p.id);
                  await load(sportId);
                }}
              />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function PresetCard({
  preset,
  onEdit,
  onDeleted,
}: {
  preset: RulePreset;
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const r = preset.config;
  return (
    <div className="card flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-lg font-semibold">
          {preset.name}
          {preset.isDefault && (
            <span className="ml-2 rounded-full border border-brand/30 bg-brand/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-light">
              Default
            </span>
          )}
        </h3>
        <div className="flex gap-2 text-xs">
          <button className="text-muted hover:text-text" onClick={onEdit}>
            Edit
          </button>
          <button
            className="text-muted hover:text-red-400"
            onClick={onDeleted}
          >
            Delete
          </button>
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
        <Row k="Periods">
          {r.periodCount} × {fmtSec(r.periodLengthSeconds)}
        </Row>
        <Row k="Overtime">{fmtSec(r.overtimeLengthSeconds)}</Row>
        <Row k="Timeouts">{r.initialTimeouts} / team</Row>
        <Row k="Bonus at">{r.bonusFoulThreshold} fouls</Row>
        <Row k="Foul out">{r.personalFoulLimit} PF</Row>
        <Row k="Shot clock">
          {r.shotClock.enabled
            ? `${r.shotClock.durationSeconds}s / ${r.shotClock.offensiveReboundSeconds}s OR`
            : 'Off'}
        </Row>
      </dl>
    </div>
  );
}

function Row({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted">{k}</dt>
      <dd className="text-right text-text">{children}</dd>
    </>
  );
}

function fmtSec(s: number): string {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem === 0 ? `${m} min` : `${m}:${String(rem).padStart(2, '0')}`;
}

function PresetEditor({
  sportId,
  preset,
  onDone,
  onCancel,
}: {
  sportId: string;
  preset: RulePreset | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const initial = preset?.config ?? defaultRules();
  const [name, setName] = useState(preset?.name ?? '');
  const [isDefault, setIsDefault] = useState(preset?.isDefault ?? false);
  const [r, setR] = useState<BasketballRules>(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function n<K extends keyof BasketballRules>(k: K, v: BasketballRules[K]) {
    setR((p) => ({ ...p, [k]: v }));
  }
  function shot<K extends keyof BasketballRules['shotClock']>(
    k: K,
    v: BasketballRules['shotClock'][K],
  ) {
    setR((p) => ({ ...p, shotClock: { ...p.shotClock, [k]: v } }));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      if (preset) {
        await api.updateRulePreset(preset.id, {
          name,
          config: r as unknown as Record<string, unknown>,
          isDefault,
        });
      } else {
        await api.createRulePreset({
          sportId,
          name,
          config: r as unknown as Record<string, unknown>,
          isDefault,
        });
      }
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'failed_to_save');
    } finally {
      setBusy(false);
    }
  }

  const pMin = Math.floor(r.periodLengthSeconds / 60);
  const pSec = r.periodLengthSeconds % 60;
  const otMin = Math.floor(r.overtimeLengthSeconds / 60);
  const otSec = r.overtimeLengthSeconds % 60;

  return (
    <form onSubmit={submit} className="card space-y-4">
      <h2 className="text-lg font-semibold">
        {preset ? `Edit "${preset.name}"` : 'New level'}
      </h2>
      <div>
        <label className="label">Name</label>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="HS Junior Varsity, Adult Rec League, etc."
          required
          minLength={2}
        />
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <NumInput
          label="Periods"
          value={r.periodCount}
          onChange={(v) => n('periodCount', v)}
          min={1}
          max={8}
        />
        <div>
          <label className="label">Period length</label>
          <div className="flex gap-1">
            <input
              type="number"
              className="input w-16 text-center"
              value={pMin}
              min={0}
              max={60}
              onChange={(e) =>
                n(
                  'periodLengthSeconds',
                  (parseInt(e.target.value, 10) || 0) * 60 + pSec,
                )
              }
            />
            <span className="self-center text-muted">:</span>
            <input
              type="number"
              className="input w-16 text-center"
              value={pSec}
              min={0}
              max={59}
              onChange={(e) =>
                n(
                  'periodLengthSeconds',
                  pMin * 60 + (parseInt(e.target.value, 10) || 0),
                )
              }
            />
          </div>
        </div>
        <div>
          <label className="label">Overtime length</label>
          <div className="flex gap-1">
            <input
              type="number"
              className="input w-16 text-center"
              value={otMin}
              min={0}
              max={60}
              onChange={(e) =>
                n(
                  'overtimeLengthSeconds',
                  (parseInt(e.target.value, 10) || 0) * 60 + otSec,
                )
              }
            />
            <span className="self-center text-muted">:</span>
            <input
              type="number"
              className="input w-16 text-center"
              value={otSec}
              min={0}
              max={59}
              onChange={(e) =>
                n(
                  'overtimeLengthSeconds',
                  otMin * 60 + (parseInt(e.target.value, 10) || 0),
                )
              }
            />
          </div>
        </div>
        <NumInput
          label="Timeouts / team"
          value={r.initialTimeouts}
          onChange={(v) => n('initialTimeouts', v)}
          min={0}
          max={15}
        />
        <NumInput
          label="Bonus at … fouls"
          value={r.bonusFoulThreshold}
          onChange={(v) => n('bonusFoulThreshold', v)}
          min={1}
          max={20}
        />
        <NumInput
          label="Foul out at"
          value={r.personalFoulLimit}
          onChange={(v) => n('personalFoulLimit', v)}
          min={1}
          max={10}
        />
      </div>

      <div className="rounded-lg border border-border bg-surface p-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={r.shotClock.enabled}
            onChange={(e) => shot('enabled', e.target.checked)}
          />
          Use shot clock
        </label>
        {r.shotClock.enabled && (
          <div className="mt-3 grid grid-cols-2 gap-4">
            <NumInput
              label="Duration (s)"
              value={r.shotClock.durationSeconds}
              onChange={(v) => shot('durationSeconds', v)}
              min={1}
              max={60}
            />
            <NumInput
              label="Offensive rebound (s)"
              value={r.shotClock.offensiveReboundSeconds}
              onChange={(v) => shot('offensiveReboundSeconds', v)}
              min={1}
              max={60}
            />
          </div>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
        />
        Mark as default for this sport (pre-selected when creating a game)
      </label>

      {err && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {err}
        </div>
      )}

      <div className="flex gap-2">
        <button className="btn btn-primary" disabled={busy}>
          {busy ? 'Saving…' : preset ? 'Save changes' : 'Create level'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function NumInput({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <input
        type="number"
        className="input"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
      />
    </div>
  );
}
